/**
 * Normalize a Turkish search query so that special characters common in
 * Ottoman / classical Turkish titles don't block search results.
 *
 * Examples:
 *   "A'mâk-ı Hayâl"  →  "amaki hayal"
 *   "amakı hayal"    →  "amakı hayal"  (unchanged — already normalized)
 *   "Sîmurg"         →  "simurg"
 *
 * The backend `turkish_regex()` then handles the reverse (making each plain
 * character match its accented/special-character variants in stored data).
 */

/** Characters to strip completely (apostrophes in the middle of titles) */
const STRIP_RE = /[''ʻˈ\u2018\u2019\u02BC`]/gu;

/** Hyphens / dashes → replace with space so words stay separate */
const DASH_RE = /[-\u2013\u2014]/gu;

/** Circumflex → base vowel map */
const CIRCUMFLEX_MAP = {
  â: 'a', Â: 'a',
  î: 'i', Î: 'i',
  û: 'u', Û: 'u',
  ê: 'e', Ê: 'e',
  ô: 'o', Ô: 'o',
};

/**
 * @param {string} query  Raw user input
 * @returns {string}      Normalized query ready to send to the backend
 */
export function normalizeSearchQuery(query) {
  if (!query) return query;

  let result = query;

  // 1. Replace circumflex letters with their base equivalents
  result = result.replace(/[âÂîÎûÛêÊôÔ]/gu, (ch) => CIRCUMFLEX_MAP[ch] || ch);

  // 2. Strip apostrophes (they appear in titles like "A'mâk-ı Hayâl")
  result = result.replace(STRIP_RE, '');

  // 3. Replace hyphens/dashes with a space so word separation is preserved
  result = result.replace(DASH_RE, ' ');

  // 4. Collapse multiple spaces
  result = result.replace(/\s{2,}/g, ' ').trim();

  return result;
}
